import { ApplicationConfig, ErrorHandler, importProvidersFrom, provideZonelessChangeDetection } from '@angular/core';

import { TrackerModule } from '@origin8-web/core-ui/tracking';
import { APP_CONFIG, localStorageProvider, windowProvider } from '@origin8-web/core-ui/shared-providers';
import { provideHttpClient, withFetch } from '@angular/common/http';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { routes } from './app.routes';
import { OrganizationRouteReuseStrategy } from '../core/utils/organization-route-reuse.strategy';
import { withApolloGqlProviders } from '@origin8-web/core-ui/graphql';
import { withLoginProviders } from '@origin8-web/core-ui/login/providers';
import { provideAccess } from '@origin8-web/core-ui/login/permissions';
import { ARC_REQUIRED_PERMISSIONS } from '../core/permissions/required-permissions';
import { ArcClientEnv } from '../environments';
import { GlobalErrorHandler } from '@origin8-web/core-ui/error-handler';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { usePreset, updatePrimaryPalette } from '@primeuix/themes';

/** App primary palette (dark blue) – aligns with themes/tailwind.config.css and input focus ring */
const appPrimaryPalette = {
  50: '#f0f5ff',
  100: '#e0ebff',
  200: '#c2d8ff',
  300: '#94baff',
  400: '#5e93ff',
  500: '#386eff',
  600: '#2556eb',
  700: '#1d43bf',
  800: '#1a3791',
  900: '#172f70',
  950: '#0e1b3f',
};

export const appConfig: ApplicationConfig = {
  providers: [
    withLoginProviders(),
    provideAccess(ARC_REQUIRED_PERMISSIONS),
    withApolloGqlProviders(),
    importProvidersFrom(TrackerModule.forRoot({ anonymous: false, withGtm: false })),
    localStorageProvider,
    windowProvider,
    provideHttpClient(withFetch()),
    provideZonelessChangeDetection(),
    // provideServiceWorker('ngsw-worker.js', {
    //   enabled: environment.production,
    //   // Register the ServiceWorker as soon as the application is stable
    //   // or after 30 seconds (whichever comes first).
    //   registrationStrategy: 'registerWhenStable:30000',
    // }),
    providePrimeNG({
      theme: {
        preset: usePreset(Aura, updatePrimaryPalette(appPrimaryPalette)),
        options: {
          darkModeSelector: '[class*="app-dark"]',
        },
      },
    }),
    provideRouter(
      routes,
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      withComponentInputBinding(),
      withPreloading(PreloadAllModules),
    ),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
};

export const getAppConfig = (envVariables: ArcClientEnv) => {
  return { ...appConfig, providers: [...appConfig.providers, { provide: APP_CONFIG, useValue: envVariables }] };
};
